import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Card, Button } from '../../components/ui';
import { Icon } from '../../components/icons';
import { BRAND, getThemeColors, DENSITY_VALUES } from '../../lib/theme';
import {
  v1Users, v2Wiedervorlage,
  v1Admins, sortAdminsByFirstName, selectableAdmins,
  v1UserToCaregiver,
  phaseRuleErrors,
} from '../../lib/v1';
import type { V1Admin, V1User, PbPhase } from '../../lib/v1';
import { v2BkPhases, v2BkUsers } from '../../lib/v2bk';
import type { Density } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useIsMobile } from '../../lib/useBreakpoint';
import { CaregiverDetail } from '../betreuung/BetreuungPage/CaregiverDetail';
import { AnfrageActionsMenu, AnfrageAbbruchDialog } from '../anfragen/AnfrageAbbruchDialog';
import { PbProfileLink, PbNotesButton } from '../anfragen/PbProfileLink';
import { buildPath, navigateGlobal } from '../../lib/route';
import { careIconKeysFromProfile } from '../../components/care/CareParamIcons';
import { v2AnfrageAbbruch } from '../../lib/v1';
import { toast } from '../../lib/toast';
import {
  BK_ANFRAGEN_PER_PAGE, BK_ANFRAGEN_SORT_FIELDS,
  BK_ANFRAGEN_TABLE_HEADERS, v1UserToBkAnfrage,
} from './bkAnfragenConstants';
import type { BkAnfrage } from './bkAnfragenConstants';
import { wiedervorlageRowTone } from '../anfragen/anfragenConstants';
import { formatDate } from '../../lib/dateFormat';
import { ColumnPicker } from '../../components/ColumnPicker';
import { readAssigneeFilter, writeAssigneeFilter } from '../../lib/assigneeFilterPref';
import { useColumnPreferences } from '../../lib/useColumnPreferences';
import { rowLinkProps } from '../../lib/openLink';

// Batch-12: alle Tabellen-Spalten (für Column-Picker-Defaults).
const BK_ANFRAGEN_ALL_COLS = BK_ANFRAGEN_TABLE_HEADERS.map(h => h.key);

interface BkAnfragenPageProps {
  dark: boolean;
  density: Density;
}

/**
 * BK-Anfragen — Pipeline eingehender Betreuungskraft-Anfragen (user_role=1),
 * analog zur PB-Anfragen-Seite: Kanban-Board (Spalten = BK-Phasen aus
 * /v2/admin/bk-phases) + Tabellenansicht, serverseitige Suche/Sortierung,
 * Zuständigkeits-Filter und "Übernehmen". Eigenständiges Modul — teilt mit
 * pages/anfragen/ nur exportierte lib-Helfer.
 */
export function BkAnfragenPage({ dark, density }: BkAnfragenPageProps) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const d = DENSITY_VALUES[density];
  const isMobile = useIsMobile();

  const { user: currentUser } = useAuth();
  const { hasPermission } = usePermissions();
  const canAssign = hasPermission('anfragen.assign');
  const canCancel = hasPermission('users.write');
  const currentAdminId = currentUser?.id ?? null;

  const [items, setItems] = useState<BkAnfrage[]>([]);
  // Anfrage-ID → raw V1User (für BK-Drawer + Phasen-Zuordnung).
  const [rawUsersMap, setRawUsersMap] = useState<Record<string, V1User>>({});
  // BK-Phasen aus den Einstellungen (dynamische Board-Spalten).
  const [phases, setPhases] = useState<PbPhase[]>([]);
  // BK-Schnellansicht-Drawer (CaregiverDetail, wie in der Betreuung-Liste).
  const [drawerUser, setDrawerUser] = useState<V1User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  // Typeahead-Suchbegriff + Open-State im Zuständigkeits-Dropdown.
  const [assigneeSearch, setAssigneeSearch] = useState('');
  const [assigneeOpen, setAssigneeOpen] = useState(false);
  const assigneeDropdownRef = useRef<HTMLDivElement>(null);
  const assigneeSearchRef = useRef<HTMLInputElement>(null);
  const [tab, setTab] = useState<'board' | 'table'>('board');
  const [dragId, setDragId] = useState<string | null>(null);
  // Anfrage-Abbruch: Ziel-Anfrage des geöffneten Abbruch-Dialogs (oder null).
  const [abbruchTarget, setAbbruchTarget] = useState<{ id: string; name: string } | null>(null);
  // Debounced Suchbegriff → serverseitige Suche; Pagination über page/last_page.
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  // Sortierung (serverseitig). Default: neueste zuerst (id, absteigend).
  const [sortField, setSortField] = useState('id');
  const [sortProfile, setSortProfile] = useState(false);
  const [sortOrder, setSortOrder] = useState<1 | -1>(-1);

  // Alle Admins (für den Zuständigkeits-Multi-Filter).
  const [admins, setAdmins] = useState<V1Admin[]>([]);
  const [assignedFilter, setAssignedFilter] = useState<number[]>([]);
  const [filterInitialized, setFilterInitialized] = useState(false);
  // Auswahl pro Admin merken (erst nach der Initialisierung, sonst würde der Default überschreiben).
  useEffect(() => {
    if (filterInitialized) writeAssigneeFilter('anfragen_bk', currentAdminId, assignedFilter);
  }, [assignedFilter, filterInitialized, currentAdminId]);
  // Wiedervorlage-Chip „fällig" (heute + überfällig): läuft über den eigenen
  // BK-Endpunkt (GET /v2/admin/bk/wiedervorlage-faellig), damit er global über
  // ALLE BK-Anfragen filtert und nicht nur über die geladene Seite.
  const [onlyFaellig, setOnlyFaellig] = useState(false);

  // Batch-12: Spalten ein-/ausblendbar (persistiert je Admin) + Client-
  // Sortierung für abgeleitete Spalten ohne serverseitiges Sortierfeld.
  const [visibleCols, setVisibleCols] = useColumnPreferences('anfragen_bk.table', currentAdminId, BK_ANFRAGEN_ALL_COLS);
  const [clientSort, setClientSort] = useState<{ key: string; dir: 1 | -1 } | null>(null);

  const toggleSort = (sortKey: string) => {
    const sf = BK_ANFRAGEN_SORT_FIELDS[sortKey];
    if (!sf) {
      setClientSort(cs => cs?.key === sortKey ? { key: sortKey, dir: cs.dir === 1 ? -1 : 1 } : { key: sortKey, dir: 1 });
      return;
    }
    setClientSort(null);
    if (sortField === sf.field && sortProfile === !!sf.profile) {
      setSortOrder(o => (o === 1 ? -1 : 1));
    } else {
      setSortField(sf.field);
      setSortProfile(!!sf.profile);
      setSortOrder(1);
    }
  };

  // Zuständigkeits-Dropdown bei Außenklick schließen.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (assigneeDropdownRef.current && !assigneeDropdownRef.current.contains(e.target as Node)) {
        setAssigneeOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Suchfeld fokussieren, wenn das Dropdown öffnet.
  useEffect(() => {
    if (assigneeOpen) {
      const timer = setTimeout(() => assigneeSearchRef.current?.focus(), 30);
      return () => clearTimeout(timer);
    }
  }, [assigneeOpen]);

  // BK-Phasen laden (Board-Spalten).
  useEffect(() => {
    v2BkPhases.board().then(setPhases).catch(e => setError(e instanceof Error ? e.message : tr('anfragen_bk.error.load')));
  }, []);

  // Admins für das Zuständigkeits-Dropdown laden (sortiert nach Name).
  useEffect(() => {
    let alive = true;
    v1Admins.list()
      .then(list => {
        if (!alive) return;
        const sorted = sortAdminsByFirstName(list);
        setAdmins(sorted);
      })
      .catch(() => { /* silent */ });
    return () => { alive = false; };
  }, []);

  // Sobald der aktuelle Admin bekannt ist: Default-Filter = "ich".
  useEffect(() => {
    if (!filterInitialized && currentAdminId !== null) {
      setAssignedFilter(readAssigneeFilter('anfragen_bk', currentAdminId) ?? [currentAdminId]);
      setFilterInitialized(true);
    }
  }, [currentAdminId, filterInitialized]);

  // Debounce der Sucheingabe (350 ms) in `query`.
  useEffect(() => {
    const id = setTimeout(() => setQuery(search.trim()), 350);
    return () => clearTimeout(id);
  }, [search]);

  // BK laden (= Anfragen von Betreuungskräften). Seite 1 neu laden, sobald
  // sich Suche/Sortierung ändert; die serverseitige Suche findet ALLE BK.
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = onlyFaellig
          ? await v2Wiedervorlage.faellig('due', { page: 1, per_page: BK_ANFRAGEN_PER_PAGE }, 'bk')
          : await v1Users.byRole('BK', {
              page: 1, perPage: BK_ANFRAGEN_PER_PAGE,
              // Abgebrochene Anfragen (negative Phase) bleiben draußen.
              filters: { ...(query ? { search: query } : {}), excludeCancelled: 1 },
              orderBy: { field: sortField, profile: sortProfile }, order: sortOrder,
            });
        if (!alive) return;
        setItems(res.data.map(v1UserToBkAnfrage));
        const rawMap: Record<string, V1User> = {};
        res.data.forEach(u => { rawMap[`BK-${u.id}`] = u; });
        setRawUsersMap(rawMap);
        setPage(res.current_page);
        setLastPage(res.last_page);
        setTotal(res.total);
      } catch (e) {
        if (!alive) return;
        setError(e instanceof Error ? e.message : tr('anfragen_bk.error.load'));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [query, sortField, sortProfile, sortOrder, onlyFaellig]);

  // Hängt die nächste Seite an (behält Suche/Sortierung bei).
  const loadMore = async () => {
    if (loadingMore || page >= lastPage) return;
    setLoadingMore(true);
    try {
      const res = onlyFaellig
        ? await v2Wiedervorlage.faellig('due', { page: page + 1, per_page: BK_ANFRAGEN_PER_PAGE }, 'bk')
        : await v1Users.byRole('BK', {
            page: page + 1, perPage: BK_ANFRAGEN_PER_PAGE,
            filters: { ...(query ? { search: query } : {}), excludeCancelled: 1 },
            orderBy: { field: sortField, profile: sortProfile }, order: sortOrder,
          });
      setItems(prev => [...prev, ...res.data.map(v1UserToBkAnfrage)]);
      setRawUsersMap(prev => {
        const next = { ...prev };
        res.data.forEach(u => { next[`BK-${u.id}`] = u; });
        return next;
      });
      setPage(res.current_page);
      setLastPage(res.last_page);
      setTotal(res.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : tr('anfragen_bk.error.load'));
    } finally {
      setLoadingMore(false);
    }
  };

  // Klick auf Karte/Zeile: öffnet die BK-Schnellansicht (CaregiverDetail).
  const openBk = (anfrageId: string) => {
    const raw = rawUsersMap[anfrageId];
    if (!raw) return;
    setDrawerUser(raw);
  };

  // Shortcuts auf Karte/Zeile (analog PB-Anfragen): Profil öffnet die volle
  // BK-Detailseite (/betreuung/{id}), Notizen direkt deren Notizen-Tab.
  const bkHref = (anfrageId: string, tab?: string): string | null => {
    const raw = rawUsersMap[anfrageId];
    return raw ? buildPath('betreuung', String(raw.id), tab ?? null) : null;
  };
  const openBkDetail = (anfrageId: string, tab?: string) => {
    const raw = rawUsersMap[anfrageId];
    if (raw) navigateGlobal('betreuung', String(raw.id), tab ?? null);
  };

  // Wiedervorlage im Drawer gespeichert/gelöscht/erledigt: Liste, Roh-User und
  // Drawer-User auf den neuen Spiegel ziehen (Zeilentönung/Badge sofort aktuell).
  const handleWiedervorlageSaved = (u: V1User) => {
    const key = `BK-${u.id}`;
    setRawUsersMap(prev => prev[key]
      ? { ...prev, [key]: { ...prev[key], wiedervorlage_am: u.wiedervorlage_am ?? null, wiedervorlage_notiz: u.wiedervorlage_notiz ?? null } }
      : prev);
    setItems(prev => prev.map(it => it.id === key
      ? { ...it, wiedervorlage_am: u.wiedervorlage_am ?? null, wiedervorlage_notiz: u.wiedervorlage_notiz ?? null }
      : it));
    setDrawerUser(prev => prev && prev.id === u.id
      ? { ...prev, wiedervorlage_am: u.wiedervorlage_am ?? null, wiedervorlage_notiz: u.wiedervorlage_notiz ?? null }
      : prev);
  };

  // "Übernehmen": weist die Anfrage dem aktuellen Admin zu (v2-Claim-Endpoint,
  // user-generisch — Backend unterscheidet PB/BK über user_role).
  const claim = async (anfrageId: string) => {
    const rawId = Number(anfrageId.replace('BK-', ''));
    try {
      const result = await v1Users.claimAnfrage(rawId);
      setItems(prev => prev.map(it =>
        it.id === anfrageId
          ? { ...it, assigned_to: result.assigned_to, assigned_to_name: result.assigned_to_name }
          : it
      ));
      // Auto-Advance des Regelsystems: Karte in die neue Phase verschieben.
      if (result.phase != null) {
        setRawUsersMap(prev => prev[anfrageId]
          ? { ...prev, [anfrageId]: { ...prev[anfrageId], phase: result.phase, assigned_to: result.assigned_to } }
          : prev);
      }
    } catch { /* silent — Toast könnte ergänzt werden */ }
  };

  // Zuständigkeits-Filter als clientseitige Verfeinerung über die geladenen
  // Seiten. Phase-1-Anfragen (noch keine Phase gesetzt) und noch nicht
  // zugewiesene Anfragen sind für alle sichtbar — sonst verschwindet eine
  // unzugewiesene Karte beim Verschieben aus Phase 1 sofort vom Board.
  const filtered = items.filter(i => {
    const raw = rawUsersMap[i.id];
    const isPhase1 = !raw?.phase || (phases.length > 0 && raw.phase === phases[0]?.id);
    if (isPhase1) return true;
    if (i.assigned_to == null) return true;
    if (assignedFilter.length > 0 && !assignedFilter.includes(i.assigned_to)) return false;
    return true;
  });

  // Batch-12: Client-Sortierung über die geladenen Zeilen (assigned_to o.ä.).
  const clientSortValue = (r: BkAnfrage, key: string): string => {
    switch (key) {
      case 'assigned_to': return r.assigned_to_name ?? '';
      default: return '';
    }
  };
  const tableRows = clientSort
    ? [...filtered].sort((a, b) =>
        clientSortValue(a, clientSort.key).localeCompare(clientSortValue(b, clientSort.key), 'de') * clientSort.dir)
    : filtered;
  const tableCols = BK_ANFRAGEN_TABLE_HEADERS.filter(h => visibleCols.includes(h.key));
  const showCol = (k: string) => visibleCols.includes(k);

  // Typeahead-Filter der Admin-Liste (Name + E-Mail) im Zuständigkeits-Dropdown.
  const filteredAdmins = useMemo(() => {
    const q = assigneeSearch.trim().toLowerCase();
    // Nur aktive Mitarbeiter wählbar; bereits gesetzte Filterwerte bleiben sichtbar.
    const options = selectableAdmins(admins, assignedFilter);
    if (!q) return options;
    return options.filter(a => {
      const name = [a.vorname, a.nachname].filter(Boolean).join(' ').toLowerCase();
      return name.includes(q) || (a.email ?? '').toLowerCase().includes(q);
    });
  }, [admins, assigneeSearch, assignedFilter]);

  // Drag&Drop: Karte in eine andere Phasen-Spalte ziehen → persistiert via
  // PATCH /v2/admin/bk-users/{id}/phase (BK-eigene additive Backend-Route,
  // validiert gegen bk_phase_rules). Optimistisches Update mit Rollback;
  // bei E_PHASE_RULES erscheinen die unerfüllten Bedingungen als Toast.
  const handleDropOnPhase = async (targetPhase: PbPhase) => {
    const id = dragId;
    setDragId(null);
    if (!id) return;
    const raw = rawUsersMap[id];
    if (!raw || raw.phase === targetPhase.id) return;
    const rawId = Number(id.replace('BK-', ''));
    setRawUsersMap(prev => ({ ...prev, [id]: { ...raw, phase: targetPhase.id } }));
    try {
      await v2BkUsers.setPhase(rawId, { phase: targetPhase.id });
    } catch (e) {
      setRawUsersMap(prev => ({ ...prev, [id]: raw }));
      const unmet = phaseRuleErrors(e);
      if (unmet) {
        toast(unmet, 'warning', tr('anfragen_bk.phase.rules_unmet_title'));
      }
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: d.gap, height: '100%' }}>
      {error && (
        <Card dark={dark} style={{ padding: 12, borderColor: '#f04438' }}>
          <div style={{ fontSize: 13, color: '#b42318', fontWeight: 500 }}>
            {tr('anfragen_bk.error.load')}: {error}
          </div>
        </Card>
      )}

      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', background: t.card, border: `1px solid ${t.border}`, borderRadius: 9, padding: 2 }}>
          {[
            { k: 'board' as const, l: tr('anfragen_bk.list.view.board'), Ic: Icon.Board },
            { k: 'table' as const, l: tr('anfragen_bk.list.view.table'), Ic: Icon.List },
          ].map(o => (
            <button key={o.k} onClick={() => setTab(o.k)} style={{
              padding: '6px 12px', borderRadius: 7,
              background: tab === o.k ? '#fce7f4' : 'transparent',
              color: tab === o.k ? BRAND : t.muted,
              border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 500,
              display: 'inline-flex', alignItems: 'center', gap: 6,
              fontFamily: 'Outfit, sans-serif',
            }}>
              <o.Ic c={tab === o.k ? BRAND : t.muted}/>{o.l}
            </button>
          ))}
        </div>

        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <span style={{ position: 'absolute', left: 10 }}><Icon.Search c={t.placeholder} size={14}/></span>
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder={tr('common.search.placeholder')}
            style={{ fontSize: 12, border: `1px solid ${t.border}`, borderRadius: 8, padding: '7px 12px 7px 32px', width: 220, background: t.inputBg, color: t.text, fontFamily: 'Outfit,sans-serif', outline: 'none' }}
          />
        </div>

        {/* Wiedervorlage-Chip: heute fällig + überfällig, global (v2) */}
        <button
          type="button"
          onClick={() => setOnlyFaellig(v => !v)}
          aria-pressed={onlyFaellig}
          title={tr('anfragen_bk.filter.wiedervorlage_faellig_hint')}
          style={{
            fontSize: 12, fontWeight: 600, border: `1px solid ${onlyFaellig ? '#b42318' : t.border}`,
            borderRadius: 8, padding: '7px 12px', cursor: 'pointer', fontFamily: 'Outfit,sans-serif',
            background: onlyFaellig ? '#fef3f2' : t.inputBg, color: onlyFaellig ? '#b42318' : t.text,
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}
        >
          <Icon.Bell c={onlyFaellig ? '#b42318' : t.muted}/>
          {tr('anfragen_bk.filter.wiedervorlage_faellig')}
        </button>

        {/* Batch-12: Spalten ein-/ausblenden (nur Tabellenansicht) */}
        {tab === 'table' && (
          <ColumnPicker
            dark={dark}
            columns={BK_ANFRAGEN_TABLE_HEADERS.map(h => ({ id: h.key, label: tr(`anfragen_bk.list.header.${h.key}`) }))}
            visible={visibleCols}
            onChange={setVisibleCols}
          />
        )}

        {/* Zuständigkeits-Multi-Filter — Dropdown mit Außenklick-Schließen */}
        <div ref={assigneeDropdownRef} style={{ position: 'relative', display: 'inline-block' }}>
          <button
            type="button"
            aria-label={tr('anfragen_bk.filter.assignee_aria')}
            onClick={() => setAssigneeOpen(o => !o)}
            style={{
              fontSize: 12, border: `1px solid ${t.border}`, borderRadius: 8,
              padding: '7px 10px', background: t.inputBg, color: t.text,
              fontFamily: 'Outfit,sans-serif', cursor: 'pointer',
            }}
          >
            {assignedFilter.length === 0
              ? (tr('anfragen_bk.filter.assigned_all') || 'Alle Zugewiesen')
              : assignedFilter.length === 1
                ? (() => {
                    const found = admins.find(a => a.id === assignedFilter[0]);
                    return found
                      ? [found.vorname, found.nachname].filter(Boolean).join(' ')
                      : (tr('anfragen_bk.filter.assigned_me') || 'Ich');
                  })()
                : (tr('anfragen_bk.filter.assigned_count', { count: assignedFilter.length }) || `${assignedFilter.length} Admins`)
            }
          </button>
          {assigneeOpen && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, zIndex: 100,
              background: t.card, border: `1px solid ${t.border}`, borderRadius: 8,
              padding: '6px 0', minWidth: 220, boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              marginTop: 4,
            }}>
              <div style={{ padding: '4px 12px 6px', position: 'relative', display: 'flex', alignItems: 'center' }}>
                <span style={{ position: 'absolute', left: 20, display: 'flex' }}><Icon.Search c={t.placeholder} size={13}/></span>
                <input
                  ref={assigneeSearchRef}
                  type="text"
                  aria-label={tr('anfragen_bk.filter.assigned_search')}
                  placeholder={tr('anfragen_bk.filter.assigned_search')}
                  value={assigneeSearch}
                  onChange={e => setAssigneeSearch(e.target.value)}
                  style={{
                    fontSize: 12, border: `1px solid ${t.border}`, borderRadius: 7,
                    padding: '6px 10px 6px 28px', width: '100%', boxSizing: 'border-box',
                    background: t.inputBg, color: t.text, outline: 'none', fontFamily: 'Outfit,sans-serif',
                  }}
                />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 12px', cursor: 'pointer', fontSize: 12, color: t.text }}>
                <input type="checkbox"
                  checked={assignedFilter.length === 0}
                  onChange={() => setAssignedFilter([])}
                />
                {tr('anfragen_bk.filter.assigned_all') || 'Alle Zugewiesen'}
              </label>
              {admins.length > 0 && filteredAdmins.length === 0 && (
                <div style={{ padding: '6px 12px', fontSize: 12, color: t.muted }}>
                  {tr('common.search.empty')}
                </div>
              )}
              {filteredAdmins.map(a => {
                const label = [a.vorname, a.nachname].filter(Boolean).join(' ').trim() || a.email;
                const checked = assignedFilter.includes(a.id);
                return (
                  <label key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 12px', cursor: 'pointer', fontSize: 12, color: t.text }}>
                    <input type="checkbox"
                      checked={checked}
                      onChange={() => setAssignedFilter(prev =>
                        checked ? prev.filter(id => id !== a.id) : [...prev, a.id]
                      )}
                    />
                    {label}
                    {a.id === currentAdminId && (
                      <span style={{ fontSize: 10, color: t.muted, marginLeft: 4 }}>
                        ({tr('anfragen_bk.filter.assigned_me_suffix') || 'ich'})
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Board-Ansicht */}
      {tab === 'board' && (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.max(phases.length, 1)}, 1fr)`, gap: 10, flex: 1, minHeight: 0, overflowX: 'auto' }}>
          {phases.map((phase, phaseIdx) => {
            const col = filtered.filter(i => {
              const raw = rawUsersMap[i.id];
              const userPhase = raw?.phase ?? 0;
              // Einträge ohne Phase fallen in Spalte 0 (Phase 1).
              return userPhase === phase.id || (phaseIdx === 0 && !userPhase);
            });
            return (
              <div
                key={phase.id}
                onDragOver={e => e.preventDefault()}
                onDrop={() => void handleDropOnPhase(phase)}
                style={{ background: t.cardMuted, border: `1px solid ${t.border}`, borderRadius: 12, padding: 10, display: 'flex', flexDirection: 'column', minWidth: 200 }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, paddingLeft: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: phase.color }}/>
                    <span style={{ fontSize: 12, fontWeight: 600, color: t.heading }}>{phase.title}</span>
                  </div>
                  <span style={{ fontSize: 11, color: t.muted, background: t.card, borderRadius: 9999, padding: '1px 8px', border: `1px solid ${t.border}` }}>{col.length}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, overflowY: 'auto', flex: 1 }}>
                  {loading && col.length === 0 && (
                    <div style={{ fontSize: 11, color: t.muted, padding: '6px 4px' }}>{tr('common.loading_short')}</div>
                  )}
                  {col.map(item => (
                    <div
                      key={item.id}
                      draggable
                      onDragStart={() => setDragId(item.id)}
                      onDragEnd={() => setDragId(null)}
                      {...rowLinkProps(bkHref(item.id), () => openBk(item.id))}
                      data-wiedervorlage={wiedervorlageRowTone(item.wiedervorlage_am, dark) ? 'true' : undefined}
                      style={{ background: wiedervorlageRowTone(item.wiedervorlage_am, dark)?.bg ?? t.card, border: `1px solid ${wiedervorlageRowTone(item.wiedervorlage_am, dark)?.border ?? t.border}`, borderRadius: 10, padding: 12, cursor: 'pointer', opacity: dragId === item.id ? 0.5 : 1, transition: 'box-shadow 0.15s' }}
                      onMouseEnter={e => (e.currentTarget as HTMLDivElement).style.boxShadow = dark ? '0 4px 10px rgba(0,0,0,0.3)' : '0 4px 10px -2px rgba(16,24,40,0.08)'}
                      onMouseLeave={e => (e.currentTarget as HTMLDivElement).style.boxShadow = 'none'}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                        <span style={{ fontSize: 10, color: t.muted, fontWeight: 600 }}>{item.id}</span>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <PbNotesButton href={bkHref(item.id, 'aufgaben-notizen')} dark={dark} onOpen={() => openBkDetail(item.id, 'aufgaben-notizen')} />
                          <PbProfileLink href={bkHref(item.id)} dark={dark} onOpen={() => openBkDetail(item.id)} />
                          {canCancel && (
                            <AnfrageActionsMenu dark={dark} onAbbrechen={() => setAbbruchTarget({ id: item.id, name: item.name })} />
                          )}
                        </div>
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: t.heading, marginBottom: 2 }}>{item.name}</div>
                      <div style={{ fontSize: 11, color: t.muted, display: 'flex', alignItems: 'center', gap: 5, marginBottom: 8 }}>
                        <Icon.MapPin c={t.muted}/>{item.city || '—'}
                      </div>
                      <div style={{ fontSize: 11, color: t.text, background: t.cardMuted, borderRadius: 6, padding: '4px 8px', marginBottom: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.contact}</div>
                      <div style={{ fontSize: 10, color: t.muted }}>{tr(item.received.key, { count: item.received.count })}</div>
                      {/* Wiedervorlage-Badge: erreicht = gelb, überschritten = rot */}
                      {item.wiedervorlage_am && (() => {
                        const tone = wiedervorlageRowTone(item.wiedervorlage_am, dark);
                        return (
                          <div
                            title={item.wiedervorlage_notiz || undefined}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 4, marginTop: 6, fontSize: 10,
                              color: tone ? tone.text : t.muted, fontWeight: tone ? 600 : 400,
                            }}
                          >
                            <Icon.Bell c={tone ? tone.text : t.muted}/>
                            {formatDate(item.wiedervorlage_am)}
                          </div>
                        );
                      })()}
                      {/* Zuständigkeits-Zeile */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6, paddingTop: 6, borderTop: `1px solid ${t.borderSoft}` }}>
                        <span style={{ fontSize: 10, color: t.muted }}>
                          {item.assigned_to_name
                            ? (tr('anfragen_bk.card.assigned_to', { name: item.assigned_to_name }) || `Zugewiesen: ${item.assigned_to_name}`)
                            : (tr('anfragen_bk.card.unassigned') || 'Nicht zugewiesen')}
                        </span>
                        {canAssign && (item.assigned_to == null || item.assigned_to !== currentAdminId) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              void claim(item.id);
                            }}
                            style={{
                              fontSize: 10, fontWeight: 600,
                              background: BRAND, color: '#fff',
                              border: 'none', borderRadius: 6,
                              padding: '3px 8px', cursor: 'pointer',
                              fontFamily: 'Outfit,sans-serif',
                            }}
                          >
                            {tr('anfragen_bk.action.claim') || 'Übernehmen'}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Tabellen-Ansicht */}
      {tab === 'table' && (
        <Card dark={dark} style={{ overflow: 'hidden', flex: 1, display: 'flex', flexDirection: 'column' }}>
          <div style={{ overflow: 'auto', flex: 1 }}>
            <table style={{ width: '100%', minWidth: 720, borderCollapse: 'collapse', fontSize: 13 }}>
              <thead style={{ position: 'sticky', top: 0, background: t.tableHeader, zIndex: 1 }}>
                <tr>
                  {tableCols.map(h => {
                    const sk = h.sortKey;
                    const sf = sk ? BK_ANFRAGEN_SORT_FIELDS[sk] : undefined;
                    const serverActive = !!sf && sortField === sf.field && sortProfile === !!sf.profile;
                    const clientActive = !sf && clientSort?.key === h.key;
                    const active = serverActive || clientActive;
                    const dir = serverActive ? sortOrder : (clientSort?.dir ?? 1);
                    return (
                      <th
                        key={h.key}
                        onClick={() => toggleSort(sk ?? h.key)}
                        style={{ padding: `${d.rowY}px ${d.cellX}px`, textAlign: 'left', fontWeight: 600, fontSize: 11, color: active ? t.heading : t.muted, textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: `1px solid ${t.border}`, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                      >
                        <span>{tr(`anfragen_bk.list.header.${h.key}`)}</span>
                        {active && <span style={{ marginLeft: 4 }}>{dir === 1 ? '▲' : '▼'}</span>}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {loading && filtered.length === 0 && (
                  <tr>
                    <td colSpan={tableCols.length} style={{ padding: 20, color: t.muted, fontSize: 12, textAlign: 'center' }}>
                      {tr('anfragen_bk.list.loading')}
                    </td>
                  </tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr>
                    <td colSpan={tableCols.length} style={{ padding: 20, color: t.muted, fontSize: 12, textAlign: 'center' }}>
                      {tr('anfragen_bk.list.empty')}
                    </td>
                  </tr>
                )}
                {tableRows.map(r => {
                  // Wiedervorlage erreicht = gelb, überschritten = rot (ganze Zeile).
                  const tone = wiedervorlageRowTone(r.wiedervorlage_am, dark);
                  const rowBg = tone ? tone.bg : 'transparent';
                  return (
                  <tr
                    key={r.id}
                    {...rowLinkProps(bkHref(r.id), () => openBk(r.id))}
                    data-wiedervorlage={tone ? 'true' : undefined}
                    style={{ borderBottom: `1px solid ${t.borderSoft}`, cursor: 'pointer', background: rowBg }}
                    onMouseEnter={e => (e.currentTarget as HTMLTableRowElement).style.background = t.hover}
                    onMouseLeave={e => (e.currentTarget as HTMLTableRowElement).style.background = rowBg}
                  >
                    {showCol('id') && <td style={{ padding: `${d.rowY}px ${d.cellX}px`, color: t.muted, fontWeight: 600, fontSize: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {r.id}
                        <PbNotesButton href={bkHref(r.id, 'aufgaben-notizen')} dark={dark} onOpen={() => openBkDetail(r.id, 'aufgaben-notizen')} />
                        <PbProfileLink href={bkHref(r.id)} dark={dark} onOpen={() => openBkDetail(r.id)} />
                        {canCancel && (
                          <AnfrageActionsMenu dark={dark} onAbbrechen={() => setAbbruchTarget({ id: r.id, name: r.name })} />
                        )}
                      </div>
                    </td>}
                    {showCol('name') && <td style={{ padding: `${d.rowY}px ${d.cellX}px` }}>
                      <div style={{ fontWeight: 600, color: t.heading }}>{r.name}</div>
                      <div style={{ fontSize: 11, color: t.muted, marginTop: 1 }}>{r.contact}</div>
                    </td>}
                    {showCol('city') && <td style={{ padding: `${d.rowY}px ${d.cellX}px`, color: t.text }}>{r.city || '—'}</td>}
                    {showCol('status') && <td style={{ padding: `${d.rowY}px ${d.cellX}px` }}>
                      {(() => {
                        const raw = rawUsersMap[r.id];
                        const userPhase = typeof raw?.phase === 'number' ? raw.phase : 0;
                        const ph = phases.find(p => p.id === userPhase) ?? phases[0];
                        return ph
                          ? <span style={{ fontSize: 11, fontWeight: 600, borderRadius: 6, padding: '2px 8px', background: ph.color + '22', color: ph.color, border: `1px solid ${ph.color}55` }}>{ph.title}</span>
                          : <Badge tone="neutral" dark={dark}>—</Badge>;
                      })()}
                    </td>}
                    {showCol('received') && <td style={{ padding: `${d.rowY}px ${d.cellX}px`, color: t.muted, fontSize: 12 }}>{tr(r.received.key, { count: r.received.count })}</td>}
                    {showCol('wiedervorlage') && <td style={{ padding: `${d.rowY}px ${d.cellX}px`, maxWidth: 160 }}>
                      {r.wiedervorlage_am ? (
                        <div
                          title={r.wiedervorlage_notiz || undefined}
                          style={{ fontSize: 12, color: tone ? tone.text : t.text, fontWeight: tone ? 600 : 400, display: 'flex', flexDirection: 'column', gap: 1 }}
                        >
                          <span>{formatDate(r.wiedervorlage_am)}</span>
                          {r.wiedervorlage_notiz && (
                            <span style={{ fontSize: 11, color: tone ? tone.text : t.muted, fontWeight: 400, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {r.wiedervorlage_notiz}
                            </span>
                          )}
                        </div>
                      ) : <span style={{ fontSize: 12, color: t.muted }}>—</span>}
                    </td>}
                    {showCol('assigned_to') && <td style={{ padding: `${d.rowY}px ${d.cellX}px` }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 12, color: t.muted }}>
                          {r.assigned_to_name || '—'}
                        </span>
                        {canAssign && (r.assigned_to == null || r.assigned_to !== currentAdminId) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              void claim(r.id);
                            }}
                            style={{
                              fontSize: 10, fontWeight: 600,
                              background: BRAND, color: '#fff',
                              border: 'none', borderRadius: 6,
                              padding: '2px 7px', cursor: 'pointer',
                              fontFamily: 'Outfit,sans-serif',
                            }}
                          >
                            {tr('anfragen_bk.action.claim') || 'Übernehmen'}
                          </button>
                        )}
                      </div>
                    </td>}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Pagination-Footer: Zähler + "Mehr laden" (serverseitig, Board + Tabelle). */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', flexShrink: 0 }}>
        <span style={{ fontSize: 12, color: t.muted }}>
          {tr('anfragen_bk.list.count', { shown: items.length, total })}
        </span>
        {page < lastPage && (
          <Button variant="secondary" size="sm" dark={dark} disabled={loadingMore} onClick={loadMore}>
            {loadingMore ? tr('anfragen_bk.list.loading') : tr('anfragen_bk.list.load_more')}
          </Button>
        )}
      </div>

      {/* Anfrage abbrechen — Grund wählen, dann fällt die Anfrage aus der Pipeline */}
      <AnfrageAbbruchDialog
        open={abbruchTarget !== null}
        dark={dark}
        scope="bk"
        anfrageName={abbruchTarget?.name}
        onClose={() => setAbbruchTarget(null)}
        onConfirm={async (grundId, beschreibung) => {
          if (!abbruchTarget) return;
          const targetId = abbruchTarget.id;
          const rawId = Number(targetId.replace('BK-', ''));
          await v2AnfrageAbbruch.abbrechen(rawId, {
            grund_id: grundId,
            ...(beschreibung ? { beschreibung } : {}),
          });
          setItems(prev => prev.filter(it => it.id !== targetId));
          setRawUsersMap(prev => { const next = { ...prev }; delete next[targetId]; return next; });
          setTotal(prev => Math.max(0, prev - 1));
          setAbbruchTarget(null);
          toast(tr('anfragen.abbruch.success'), 'success');
        }}
      />

      {/* BK-Schnellansicht-Drawer (CaregiverDetail) — wie in der Betreuung-Liste */}
      {drawerUser && (
        <CaregiverDetail
          key={drawerUser.id}
          item={v1UserToCaregiver(drawerUser)}
          dark={dark}
          isMobile={isMobile}
          iconKeys={careIconKeysFromProfile(drawerUser.profiles?.[0])}
          onClose={() => setDrawerUser(null)}
          wiedervorlage={{ raw: drawerUser, onSaved: handleWiedervorlageSaved }}
        />
      )}
    </div>
  );
}
